import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { csvCell } from '../utils/csv';
import { cn } from '../lib/utils';

export function DataTable({
  data,
  columns,
  emptyMessage = null,
  showGlobalSearch = true,
  searchPlaceholder = null,
  showExport = true,
  rowClassName = null,
  showPagination = true,
  pageSize = 16,
  customFilters = null,
  enableRowSelection = false,
  rowSelection = {},
  onRowSelectionChange = null,
  canSelectRow = null,
  bulkActionsBar = null,
  serverPagination = null,
  serverSorting = null,
  embedded = false,
  onRowClick = null,
}) {
  const { t } = useTranslation();
  const [localSorting, setLocalSorting] = useState([]);
  // Server-sorted tables let the caller own the sort: a header click has to reach the API, not
  // reorder the twenty rows it already returned.
  const sorting = serverSorting ? serverSorting.sorting : localSorting;
  const applySorting = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    (serverSorting ? serverSorting.onSortingChange : setLocalSorting)(next);
  };
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  // Hiding the pager does not stop the row model from slicing: without this, a caller that
  // passes showPagination={false} silently loses every row past pageSize while the footer keeps
  // announcing the real total. No pager means no pagination at all.
  const effectivePageSize = showPagination || serverPagination ? pageSize : Number.MAX_SAFE_INTEGER;
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: effectivePageSize,
  });

  // Update pagination when pageSize prop changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
      pageSize: effectivePageSize,
    }));
  }, [effectivePageSize]);

  // Auto-hide columns with meta.hidden
  const hiddenColumns = columns.reduce((acc, col) => {
    if (col.meta?.hidden) {
      acc[col.id || col.accessorKey] = false;
    }
    return acc;
  }, {});

  // Build selection column if enabled
  const selectionColumn = enableRowSelection ? [{
    id: 'select',
    meta: { width: 40 },
    header: ({ table }) => {
      const rows = table.getFilteredRowModel().rows;
      const selectableRows = canSelectRow ? rows.filter(row => canSelectRow(row.original)) : rows;
      const allSelected = selectableRows.length > 0 && selectableRows.every(row => row.getIsSelected());
      const someSelected = selectableRows.some(row => row.getIsSelected());
      return (
        <Checkbox
          checked={allSelected}
          onCheckedChange={(value) => {
            selectableRows.forEach(row => row.toggleSelected(!!value));
          }}
          aria-label={t('Select all')}
          {...(someSelected && !allSelected ? { 'data-state': 'indeterminate' } : {})}
        />
      );
    },
    cell: ({ row }) => {
      const selectable = canSelectRow ? canSelectRow(row.original) : true;
      if (!selectable) return null;
      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t('Select the row')}
        />
      );
    },
    enableSorting: false,
  }] : [];

  const allColumns = [...selectionColumn, ...columns];

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Server-paginated tables get their rows pre-sliced by the API, so react-table
    // must not slice them again; pageCount comes from the caller.
    manualPagination: !!serverPagination,
    ...(serverPagination ? { pageCount: serverPagination.pageCount ?? -1 } : {}),
    manualSorting: !!serverSorting,
    onSortingChange: applySorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    ...(enableRowSelection ? {
      enableRowSelection: canSelectRow ? (row) => canSelectRow(row.original) : true,
      onRowSelectionChange: onRowSelectionChange,
    } : {}),
    state: {
      sorting,
      globalFilter,
      columnFilters,
      pagination,
      columnVisibility: hiddenColumns,
      ...(enableRowSelection ? { rowSelection } : {}),
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: pageSize,
      },
    },
    globalFilterFn: 'includesString',
  });

  // Single descriptor for the footer controls, whether pages come from the
  // client-side row model or from the caller's server cursor.
  const pager = serverPagination
    ? {
        pageIndex: serverPagination.pageIndex,
        pageCount: serverPagination.pageCount,
        canPrevious: serverPagination.pageIndex > 0,
        canNext: !!serverPagination.hasNextPage,
        onPrevious: serverPagination.onPreviousPage,
        onNext: serverPagination.onNextPage,
        // A page in flight must not be turned again: a second click would skip one.
        busy: !!serverPagination.busy,
      }
    : showPagination && table.getPageCount() > 1
      ? {
          pageIndex: table.getState().pagination.pageIndex,
          pageCount: table.getPageCount(),
          canPrevious: table.getCanPreviousPage(),
          canNext: table.getCanNextPage(),
          onPrevious: () => table.previousPage(),
          onNext: () => table.nextPage(),
        }
      : null;

  // CSV Export function
  const exportToCSV = () => {
    const filteredRows = table.getFilteredRowModel().rows;

    if (filteredRows.length === 0) {
      alert(t('No data to export'));
      return;
    }

    // Get visible columns (exclude action and select columns)
    const visibleColumns = table.getAllColumns().filter(col =>
      col.getIsVisible() && col.id !== 'actions' && col.id !== 'select' && col.columnDef.header
    );

    // Build CSV header
    const headers = visibleColumns.map(col => {
      const header = col.columnDef.header;
      return typeof header === 'string' ? header : col.id;
    });

    // csvCell, not a local escape: it also neutralises a value Excel would evaluate as a formula
    const csvRows = filteredRows.map(row =>
      visibleColumns.map(col => {
        const cell = row.getValue(col.id);
        return cell === null || cell === undefined ? '' : csvCell(String(cell));
      })
    );

    const csvContent = [
      headers.map(csvCell).join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // One central guard rather than a stopPropagation in every caller's cell: a row is full of
  // controls that own their click — the pinned Actions pencil, the selection checkbox, the name
  // button, a link. No tabIndex or role on the <tr>: keyboard access stays those controls.
  const handleRowClick = (row, event) => {
    if (event.target.closest?.('button, a, input, select, textarea, [role="button"], [role="checkbox"]')) return;
    onRowClick(row.original);
  };

  return (
    <div className={embedded ? 'space-y-0' : 'space-y-4'}>
      {/* Global Search and Custom Filters */}
      <div className="flex gap-4 flex-col sm:flex-row">
        {showGlobalSearch && (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder={searchPlaceholder || t('Search…')}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-10"
            />
          </div>
        )}
        {customFilters && (
          <div className="flex gap-2 flex-wrap">
            {customFilters({ table, columnFilters, setColumnFilters })}
          </div>
        )}
        {/* Opt-out, default on: a referential is administered, not exported. */}
        {showExport && (
          <Button
            onClick={exportToCSV}
            variant="outline"
            className="flex items-center gap-2 whitespace-nowrap"
          >
            <Download className="w-4 h-4" />
            {t('Export CSV')}
          </Button>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {enableRowSelection && bulkActionsBar && Object.keys(rowSelection).length > 0 && (
        bulkActionsBar({ table, selectedCount: Object.keys(rowSelection).length })
      )}

      {/* Table */}
      {/* Embedded: the card frames the table, so no inner box. Parent `[&_th]`/`[&_td]` selectors
          outrank table.jsx's own paddings without touching it; `table-fixed` turns meta widths into proportions. */}
      <div className={cn(!embedded && 'rounded-md border', 'overflow-x-auto')}>
        <Table
          className={cn(
            embedded &&
              'table-fixed min-w-[1098px] [&_thead_tr]:bg-background [&_th]:h-[42px] [&_th]:px-[9px] [&_th]:text-xs [&_th]:font-semibold [&_td]:px-[9px] [&_td]:py-[13px] [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-[18px] [&_td:last-child]:pr-[18px]'
          )}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  // meta.width, never columnDef.size: react-table merges a default `size: 150`
                  // into every column, so reading it pinned every column of every table in the
                  // app to 150px — the `width ? …` guard below could never fall through.
                  const { width, cellClassName } = header.column.columnDef.meta ?? {};
                  const sorted = header.column.getIsSorted();
                  const label = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  return (
                    <TableHead
                      key={header.id}
                      className={cellClassName}
                      style={width ? { width: `${width}px` } : undefined}
                      aria-sort={
                        header.column.getCanSort()
                          ? { asc: 'ascending', desc: 'descending' }[sorted] ?? 'none'
                          : undefined
                      }
                    >
                      {/* A button, not a clickable div: Enter and Space come free, and the
                          control announces itself instead of being mouse-only. */}
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="flex items-center gap-2 select-none"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {label}
                          <span className="text-gray-400">
                            {{
                              asc: <ChevronUp className="w-4 h-4" />,
                              desc: <ChevronDown className="w-4 h-4" />,
                            }[sorted] ?? (
                              <div className="flex flex-col">
                                <ChevronUp className="w-3 h-3 -mb-1" />
                                <ChevronDown className="w-3 h-3" />
                              </div>
                            )}
                          </span>
                        </button>
                      ) : (
                        label
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={onRowClick ? (event) => handleRowClick(row, event) : undefined}
                  // `group/row` so a column pinned with meta.cellClassName can follow the row's
                  // own hover: an opaque sticky cell otherwise leaves the row half-highlighted.
                  className={cn('group/row', onRowClick && 'cursor-pointer', rowClassName?.(row.original))}
                >
                  {row.getVisibleCells().map((cell) => {
                    const { width, cellClassName } = cell.column.columnDef.meta ?? {};
                    return (
                      <TableCell
                        key={cell.id}
                        className={cellClassName}
                        style={width ? { width: `${width}px` } : undefined}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={allColumns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage || t('No data available')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Results count and Pagination */}
      <div
        className={cn(
          'flex items-center justify-between',
          embedded && 'border-t px-5 py-[13px]'
        )}
      >
        <div className={cn(embedded ? 'text-[13px]' : 'text-sm', 'text-muted-foreground')}>
          {/* Server-paginated rows are one page: counting them would announce "20 of 20" while
              the API knows there are 340. The total comes from the caller or not at all. */}
          {serverPagination ? (
            // A caller whose rows are not what its total counts — one row per mission over a
            // total of requests — words the line itself rather than being announced wrong.
            serverPagination.summary ??
            (serverPagination.totalCount > 0 &&
              t('Showing {{count}} of {{total}} results', {
                count: table.getRowModel().rows.length,
                total: serverPagination.totalCount,
              }))
          ) : table.getFilteredRowModel().rows.length > 0 ? (
            <>
              {showPagination ? (
                <>
                  {t('Showing {{count}} of {{total}} results', {
                    count: table.getRowModel().rows.length,
                    total: table.getFilteredRowModel().rows.length,
                  })}
                  {globalFilter && ` ${t('(filtered from {{count}})', { count: data.length })}`}
                </>
              ) : (
                <>
                  {t('Showing {{count}} results', { count: table.getFilteredRowModel().rows.length })}
                  {globalFilter && ` ${t('(filtered from {{count}} total)', { count: data.length })}`}
                </>
              )}
            </>
          ) : null}
        </div>

        {pager && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={pager.onPrevious}
              disabled={!pager.canPrevious || pager.busy}
            >
              {t('Previous')}
            </Button>
            <div className={cn(embedded ? 'px-1 text-[13px]' : 'text-sm', 'text-muted-foreground')}>
              {pager.pageCount != null
                ? t('Page {{current}} of {{total}}', {
                    current: pager.pageIndex + 1,
                    total: pager.pageCount,
                  })
                : t('Page {{current}}', { current: pager.pageIndex + 1 })}
            </div>
            <Button variant="outline" size="sm" onClick={pager.onNext} disabled={!pager.canNext || pager.busy}>
              {t('Next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
